import { BookmarkModel } from '../models/bookmark.model';
import { BookmarkEntryModel } from '../models/bookmark.entry.model';
import { StoryChapterThumbnail } from 'src/app/story/models/story-chapter-thumbnail.model';
import { BookmarkSortModel } from '../models/bookmark-sort.model';
import { WidgetOptions } from 'src/app/publish/models/widget-options.model';
import { CollectionModel } from 'src/app/organizations/models/collection.model';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { StoryModel } from 'src/app/story/models/story.model';
import { BookmarkGroupModel } from '../models/bookmark.group.model';

export class LoadBookmarks {
  static readonly type = '[Bookmarks] Load';
  constructor(public query: string) { }
}

export class LoadOrganizationBookmarks {
  static readonly type = '[Bookmarks] Load Organization Bookmarks';
  constructor(public organizationId: number) { }
}

export class LoadCollectionBookmarks {
  static readonly type = '[Bookmarks] Load Collection Bookmarks';
  constructor(public organizationId: number, public collectionId: number) { }
}

export class SelectBookmark {
  static readonly type = '[Bookmarks] Select Bookmark';
  constructor(public id: string, public type: StoryType) { }
}

export class SelectBookmarkSuccess {
  static readonly type = '[Bookmarks] Select Bookmark Success';
  constructor(public bookmark: BookmarkModel) { }
}

export class LoadBookmarksSuccess {
  static readonly type = '[Bookmarks] Load Success';
  constructor(public result: BookmarkModel[]) { }
}

export class DeleteBookmark {
  static readonly type = '[Bookmarks] Delete Bookmark';
  constructor(public bookmarkId: string) { }
}

export class DeleteBookmarkSuccess {
  static readonly type = '[Bookmarks] Bookmark Delete Success';
  constructor(public bookmarkId: string) { }
}

export class DeleteBookmarkFail {
  static readonly type = '[Bookmarks] Bookmark Delete Fail';
  constructor(public bookmarkId: string, public message: string) { }
}

export class SaveBookmark {
  static readonly type = '[Bookmarks] Save Bookmark';
  constructor(public bookmarkEntry: BookmarkEntryModel, public inplace: boolean, public includeIndex: boolean = true) { }
}

export class SaveBookmarkSuccess {
  static readonly type = '[Bookmarks] Bookmark Saved';
  constructor(public bookmark: BookmarkModel, public story: StoryModel, public method: 'save' | 'update') { }
}

export class SaveBookmarkFail {
  static readonly type = '[Bookmarks] Bookmark Save Failed';
  constructor(public bookmarkEntry: BookmarkEntryModel) { }
}

export class DeleteFavorite {
  static readonly type = '[Bookmarks] Delete Favorite';
  constructor(public bookmark: BookmarkModel) { }
}

export class DeleteFavoriteSuccess {
  static readonly type = '[Bookmarks] Delete Favorite Success';
  constructor(public bookmarkId: string) { }
}

export class DeleteFavoriteFail {
  static readonly type = '[Bookmarks] Delete Favorite Failed';
  constructor(public bookmarkId: string) { }
}

export class SaveBookmarkThumbnails {
  static readonly type = '[Bookmarks] Save Bookmark Thumbnails';
  constructor(
    public method: string,
    public bookmarkId: string, 
    public bookmarkEntry: BookmarkEntryModel, 
    public thumbnails: Map<string, StoryChapterThumbnail>,
    public chapterIdMap?: {[id: string]: string}
    ) {}
}

export class ResetBookmarks {
  static readonly type = '[Bookmarks] Reset';
  constructor() { }
}

export class ClearBookmarks {
  static readonly type = '[Bookmarks] Clear Bookmarks';
  constructor() { }
}

export class SortBookmarks {
  static readonly type = '[Bookmarks] Sort';
  constructor(public sort: BookmarkSortModel) { }
}

export class CheckBookmark {
  static readonly type = '[Bookmarks] Check Bookmark';
  constructor(public id: string, public checked: boolean) { }
}

export class SetCheckedBookmarks {
  static readonly type = '[Bookmarks] Set Checked Bookmarks';
  constructor(public ids: string[]) { }
}

export class ExportBookmarks {
  static readonly type = '[Bookmarks] Export Bookmarks';
  constructor(
    public bookmarks: BookmarkModel[],
    public widgetURL: string,
    public widgetOptions?: WidgetOptions,
    public offlineEnabled?: boolean
  ) { }
}

export class ExportBookmarksSuccess {
  static readonly type = '[Bookmarks] Export Bookmarks Success';
  constructor(public bookmarks: BookmarkModel[]) { }
}

export class ExportBookmarksError {
  static readonly type = '[Bookmarks] Export Bookmarks Error';
  constructor(public bookmarks: BookmarkModel[]) { }
}

export class UpdateBookmarkCollection {
  static readonly type = '[Bookmarks] Update Bookmark Collection';
  constructor(
    public bookmark: BookmarkModel,
    public addCollection?: CollectionModel,
    public removeCollection?: CollectionModel) { }
}

export class UpdateBookmarkCollectionSuccess {
  static readonly type = '[Bookmarks] Update Bookmark Collection Success';
  constructor(
    public bookmark: BookmarkModel,
    public addCollection?: CollectionModel,
    public removeCollection?: CollectionModel) { }
}

export class UpdateBookmarkCollectionError {
  static readonly type = '[Bookmarks] Update Bookmark Collection Error';
  constructor(
    public bookmark: BookmarkModel,
    public addCollection?: CollectionModel,
    public removeCollection?: CollectionModel) { }
}

export class AddBookmarkCollection {
  static readonly type = '[Bookmarks] Add Bookmark Collection';
  constructor(
    public bookmark: BookmarkModel,
    public collection: CollectionModel) { }
}

export class AddBookmarkCollectionSuccess {
  static readonly type = '[Bookmarks] Add Bookmark Collection Success';
  constructor(
    public bookmark: BookmarkModel,
    public collection: CollectionModel) { }
}

export class AddBookmarkCollectionError {
  static readonly type = '[Bookmarks] Add Bookmark Collection Error';
  constructor(
    public bookmark: BookmarkModel,
    public collection: CollectionModel) { }
}

export class RemoveBookmarkCollection {
  static readonly type = '[Bookmarks] Remove Bookmark Collection';
  constructor(
    public bookmark: BookmarkModel,
    public collection: CollectionModel
  ) { }
}

export class RemoveBookmarkCollectionSuccess {
  static readonly type = '[Bookmarks] Remove Bookmark Collection Success';
  constructor(
    public bookmark: BookmarkModel,
    public collection: CollectionModel) { }
}

export class RemoveBookmarkCollectionError {
  static readonly type = '[Bookmarks] Remove Bookmark Collection Error';
  constructor(
    public bookmark: BookmarkModel,
    public collection: CollectionModel) { }
}

export class CopyBookmark {
  static readonly type = '[Bookmarks] Copy Bookmark';
  constructor(
    public id: string,
    public type: StoryType,
    public groupAccess?: BookmarkGroupModel,
    public collection?: CollectionModel) { }
}

export class CopyBookmarkSuccess {
  static readonly type = '[Bookmarks] Copy Bookmark Success';
  constructor(
    public newId: string,
    public sourceId: string,
    public groupAccess?: BookmarkGroupModel,
    public collection?: CollectionModel) { }
}

export class CopyBookmarkFailure {
  static readonly type = '[Bookmarks] Copy Bookmark Failure';
  constructor(public id: string, public error: HttpErrorResponse) { }
}

export class SetBookmarkText {
  static readonly type = '[Bookmarks] Set Bookmark Text';
  constructor(public title: string) {}
}

export class SetSelectedCategory {
  static readonly type = '[Bookmarks] Set Selected Category';
  constructor(public category: 'individual' | 'organization' | 'collection') { }
}
